import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { productUrl } from '../utils/urls';
import { optimizeImageUrl } from '../utils/imageUrl';
import { Sparkles, TrendingUp, Eye, ChevronRight, Heart } from 'lucide-react';
import { useTracking } from '../hooks/useTracking';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Source labels and icons
const SOURCE_CONFIG = {
  personalized: { label: 'Consigliati per te', icon: Sparkles, color: 'text-purple-600' },
  similar: { label: 'Potrebbe piacerti anche', icon: Eye, color: 'text-blue-600' },
  trending: { label: 'Di tendenza', icon: TrendingUp, color: 'text-orange-600' },
  complementary: { label: 'Completa il tuo ordine', icon: Sparkles, color: 'text-teal-600' },
  new: { label: 'Novità', icon: Sparkles, color: 'text-green-600' },
  popular: { label: 'I più amati', icon: Heart, color: 'text-red-500' }
};

// Compact Product Card for recommendations
const ProductCard = ({ product, variant = 'default' }) => {
  const isCompact = variant === 'compact';
  
  // Get primary image
  const primaryImage = optimizeImageUrl(product.images?.[0] || product.image_url || '/placeholder-product.jpg', 500);
  
  // Get price (products use base_price)
  const price = product.price || product.base_price || 0;
  
  // Format price
  const formatPrice = (price) => {
    if (!price || isNaN(price)) return '€ --';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  return (
    <Link 
      to={productUrl(product)}
      className={`group block bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-[#008080]/30 hover:shadow-lg transition-all duration-300 ${
        isCompact ? 'flex items-center gap-3 p-2' : ''
      }`}
    >
      {/* Image */}
      <div className={`relative overflow-hidden bg-slate-100 ${
        isCompact ? 'w-16 h-16 rounded-lg flex-shrink-0' : 'aspect-square'
      }`}>
        <img 
          src={primaryImage} 
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {product.is_new && !isCompact && (
          <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
            Nuovo
          </span>
        )}
      </div>

      {/* Content */}
      <div className={isCompact ? 'flex-1 min-w-0' : 'p-3'}>
        <h3 className={`font-medium text-slate-900 group-hover:text-[#008080] transition-colors ${
          isCompact ? 'text-sm truncate' : 'text-sm line-clamp-2 mb-1'
        }`}>
          {product.title}
        </h3>
        
        {!isCompact && product.crafter_name && (
          <p className="text-xs text-slate-500 mb-2 truncate">
            di {product.crafter_name}
          </p>
        )}
        
        <p className={`font-bold text-[#008080] ${isCompact ? 'text-sm' : 'text-base'}`}>
          {formatPrice(price)}
        </p>
      </div>
    </Link>
  );
};

export const RecommendedProducts = ({ 
  context = 'homepage',  // 'homepage' | 'product' | 'cart'
  productId = null,      // For similar products
  categorySlug = null,
  limit = 8,
  title = null,          // Custom title override
  subtitle = null,       // Optional descriptive line under the title
  variant = 'default',   // 'default' | 'compact' | 'minimal'
  className = '',
  excludeIds = []        // Product IDs to exclude (e.g., cart items)
}) => {
  const [products, setProducts] = useState([]);
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const { sessionId, userId } = useTracking();
  
  // Memoize excludeIds to prevent infinite loop
  const excludeIdsString = JSON.stringify(excludeIds);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        
        const params = new URLSearchParams({
          context,
          limit: limit.toString(),
          ...(sessionId && { session_id: sessionId }),
          ...(userId && { user_id: userId }),
          ...(productId && { product_id: productId }),
          ...(categorySlug && { category_slug: categorySlug })
        });

        const response = await fetch(`${API_URL}/api/recommendations?${params}`);
        const data = await response.json();

        if (data.products) {
          // Filter out excluded IDs
          const parsedExcludeIds = JSON.parse(excludeIdsString);
          const filtered = parsedExcludeIds.length > 0
            ? data.products.filter(p => !parsedExcludeIds.includes(p.id))
            : data.products;
          
          setProducts(filtered);
          setSource(data.source || 'popular');
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [context, productId, categorySlug, limit, sessionId, userId, excludeIdsString]);

  // Don't render if no products
  if (!loading && products.length === 0) return null;

  // Get source config
  const sourceConfig = SOURCE_CONFIG[source] || SOURCE_CONFIG.popular;
  const SourceIcon = sourceConfig.icon;
  const displayTitle = title || sourceConfig.label;

  // Loading skeleton
  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-5 h-5 bg-slate-200 rounded animate-pulse" />
          <div className="w-32 h-5 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className={`grid gap-4 ${
          variant === 'compact' 
            ? 'grid-cols-1' 
            : context === 'product' 
              ? 'grid-cols-2 md:grid-cols-4' 
              : 'grid-cols-2 md:grid-cols-4 lg:grid-cols-4'
        }`}>
          {[...Array(variant === 'compact' ? 3 : limit)].map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-xl animate-pulse" style={{ aspectRatio: variant === 'compact' ? 'auto' : '1' }}>
              {variant === 'compact' && <div className="h-16" />}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Minimal variant (for product page - very subtle)
  if (variant === 'minimal') {
    return (
      <div className={`${className}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
            <SourceIcon className={`w-4 h-4 ${sourceConfig.color}`} />
            {displayTitle}
          </h3>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {products.slice(0, 6).map((product) => (
            <Link
              key={product.id}
              to={productUrl(product)}
              className="flex-shrink-0 group"
            >
              <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 group-hover:border-[#008080]/50 transition-colors">
                <img 
                  src={optimizeImageUrl(product.images?.[0] || product.image_url || '/placeholder-product.jpg', 200)} 
                  alt={product.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <p className="text-xs text-slate-600 mt-1 w-20 truncate">{product.title}</p>
              <p className="text-xs font-semibold text-[#008080]">€{product.price || product.base_price || '--'}</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Compact variant (for cart sidebar)
  if (variant === 'compact') {
    return (
      <div className={`${className}`} data-testid="recommended-products-compact">
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-[10px] font-bold uppercase tracking-wider rounded-full">
              <Sparkles className="w-3 h-3" />
              Suggeriti
            </span>
            <h3 className="text-base font-semibold text-slate-800">
              {displayTitle}
            </h3>
          </div>
          {subtitle && (
            <p className="text-xs text-slate-500 leading-relaxed">
              {subtitle}
            </p>
          )}
        </div>
        <div className="space-y-2">
          {products.slice(0, 4).map((product) => (
            <div
              key={product.id}
              className="relative group bg-white/70 hover:bg-white rounded-lg border border-dashed border-teal-200 hover:border-teal-400 transition-all"
            >
              <ProductCard product={product} variant="compact" />
              <span
                className="absolute top-1/2 -translate-y-1/2 right-3 w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-lg font-bold opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                aria-hidden="true"
              >
                +
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default variant (for homepage)
  return (
    <div className={`${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <SourceIcon className={`w-6 h-6 ${sourceConfig.color}`} />
          {displayTitle}
        </h2>
        {context === 'homepage' && (
          <Link 
            to="/catalogo"
            className="text-sm font-medium text-[#008080] hover:text-[#006666] flex items-center gap-1 transition-colors"
          >
            Vedi tutti
            <ChevronRight className="w-4 h-4" />
          </Link>
        )}
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
};

export default RecommendedProducts;
